import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Music2, ArrowUpDown, Save, Loader2 } from 'lucide-react';
import { TranspositionSettings } from './TranspositionSettings';
import { PreviewSection } from './PreviewSection';
import type { ParsedLine } from '@/lib/leadsheetParser';

interface TranspositionToolCardProps {
  transposeBy: string;
  onTransposeByChange: (value: string) => void;
  originalKey: string;
  saving: boolean;
  onSave: () => void;
  settingsOpen: boolean;
  onSettingsOpenChange: (open: boolean) => void;
  fontSize: string;
  onFontSizeChange: (size: string) => void;
  chordFontSize: number;
  onChordFontSizeChange: (size: number) => void;
  chordColor: string;
  onChordColorChange: (color: string) => void;
  columns: string;
  onColumnsChange: (columns: string) => void;
  tekstOgAkkorder: string;
  parsedLines: ParsedLine[];
  onDownloadPDF: () => void;
}

export const TranspositionToolCard: React.FC<TranspositionToolCardProps> = ({
  transposeBy,
  onTransposeByChange,
  originalKey,
  saving,
  onSave,
  settingsOpen,
  onSettingsOpenChange,
  fontSize,
  onFontSizeChange,
  chordFontSize,
  onChordFontSizeChange,
  chordColor,
  onChordColorChange,
  columns,
  onColumnsChange,
  tekstOgAkkorder,
  parsedLines,
  onDownloadPDF,
}) => {
  const transpose = parseInt(transposeBy);

  return (
    <Card className="shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-primary to-blue-400" />
      <div className="p-6">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Music2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Transponer til ny toneart</h2>
            <p className="text-sm text-muted-foreground">Forhåndsvis og lagre transponeringer</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Velg toneart section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-base font-semibold">
              <ArrowUpDown className="h-5 w-5 text-primary" />
              Velg toneart
            </Label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Select value={transposeBy} onValueChange={onTransposeByChange}>
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="-6">C# (♭6 semitoner)</SelectItem>
                    <SelectItem value="-5">D (♭5 semitoner)</SelectItem>
                    <SelectItem value="-4">D# (♭4 semitoner)</SelectItem>
                    <SelectItem value="-3">E (♭3 semitoner)</SelectItem>
                    <SelectItem value="-2">F (♭2 semitoner)</SelectItem>
                    <SelectItem value="-1">F# (♭1 semiton)</SelectItem>
                    <SelectItem value="0">Original ({originalKey})</SelectItem>
                    <SelectItem value="1">G# (♯1 semiton)</SelectItem>
                    <SelectItem value="2">A (♯2 semitoner)</SelectItem>
                    <SelectItem value="3">A# (♯3 semitoner)</SelectItem>
                    <SelectItem value="4">B (♯4 semitoner)</SelectItem>
                    <SelectItem value="5">C (♯5 semitoner)</SelectItem>
                    <SelectItem value="6">C# (♯6 semitoner)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 sm:items-end">
                <Button
                  className="bg-status-confirmed hover:bg-status-confirmed/90 text-white gap-2 flex-1 sm:flex-none h-11"
                  onClick={onSave}
                  disabled={transposeBy === '0' || saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="hidden sm:inline">Lagrer...</span>
                      <span className="sm:hidden">Lagrer</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span className="hidden sm:inline">Lagre transponering</span>
                      <span className="sm:hidden">Lagre</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          <Separator className="my-6" />

          <TranspositionSettings
            settingsOpen={settingsOpen}
            onSettingsOpenChange={onSettingsOpenChange}
            fontSize={fontSize}
            onFontSizeChange={onFontSizeChange}
            chordFontSize={chordFontSize}
            onChordFontSizeChange={onChordFontSizeChange}
            chordColor={chordColor}
            onChordColorChange={onChordColorChange}
            columns={columns}
            onColumnsChange={onColumnsChange}
          />
        </div>

        <Separator className="my-6" />

        <PreviewSection
          tekstOgAkkorder={tekstOgAkkorder}
          parsedLines={parsedLines}
          transpose={transpose}
          fontSize={fontSize}
          chordFontSize={chordFontSize}
          chordColor={chordColor}
          onDownloadPDF={onDownloadPDF}
        />
      </div>
    </Card>
  );
};

